import { Platform, ViewStyle } from "react-native";

/**
 * Focus ring style constants for keyboard accessibility.
 * These provide visible focus indicators that meet WCAG 2.1 AA requirements.
 */
export const FOCUS_RING_WIDTH = 2;
export const FOCUS_RING_OFFSET = 2;

/**
 * Get focus ring styles for web platform.
 * Returns empty object for non-web platforms.
 *
 * @param color - The color for the focus ring (should have sufficient contrast)
 * @param width - Width of the focus ring in pixels
 * @param offset - Offset from the element edge in pixels
 */
export const getFocusRingStyle = (
  color: string,
  width: number = FOCUS_RING_WIDTH,
  offset: number = FOCUS_RING_OFFSET
): ViewStyle => {
  if (Platform.OS !== "web") {
    return {};
  }

  return {
    // @ts-ignore - Web-specific properties
    outlineStyle: "solid",
    outlineWidth: width,
    outlineColor: color,
    outlineOffset: offset,
  };
};

/**
 * Get focus ring styles that only show during keyboard navigation.
 * Use with useTabbing hook for optimal UX.
 *
 * @param isTabbing - Whether user is using keyboard navigation
 * @param isFocused - Whether the element is currently focused
 * @param color - The color for the focus ring
 */
export const getConditionalFocusStyle = (
  isTabbing: boolean,
  isFocused: boolean,
  color: string
): ViewStyle => {
  if (!isTabbing || !isFocused) {
    return {};
  }

  return getFocusRingStyle(color);
};

/**
 * CSS-in-JS mixin for focus-visible styles (for styled-components).
 * This creates styles that only appear on keyboard focus, not mouse/touch.
 *
 * @example
 * const Button = styled.Pressable`
 *   ${({ theme }) => focusVisibleMixin(theme["link-hover"])}
 * `;
 */
export const focusVisibleMixin = (focusColor: string) => {
  if (Platform.OS !== "web") {
    return "";
  }

  return `
    &:focus {
      outline: none;
    }
    &:focus-visible {
      outline: ${FOCUS_RING_WIDTH}px solid ${focusColor};
      outline-offset: ${FOCUS_RING_OFFSET}px;
    }
  `;
};

/**
 * Style object for removing default focus outline.
 * Use when implementing custom focus indicators.
 */
export const removeDefaultFocusStyle: ViewStyle =
  Platform.OS === "web"
    ? {
        // @ts-ignore - Web-specific property
        outline: "none",
      }
    : {};

export default {
  getFocusRingStyle,
  getConditionalFocusStyle,
  focusVisibleMixin,
  removeDefaultFocusStyle,
  FOCUS_RING_WIDTH,
  FOCUS_RING_OFFSET,
};
